// src/hooks/assistant/useMultiAgentSnapshots.ts
/**
 * Multi-Agent Snapshots Hook
 * 
 * Single responsibility: Managing chart state snapshots per message.
 * 
 * Key design:
 * - Only snapshots on streaming END (true -> false transition)
 * - Uses refs to avoid dependency on rapidly-changing multiAgent state
 * - Merges live state during streaming without triggering updates
 */

import { useEffect, useMemo, useRef } from "react";
import {
  useAssistantStore,
  selectMultiAgentByMessageId,
  selectIsStreaming,
  selectMultiAgent,
  selectMessages,
} from "@/stores/assistantStore";
import type { MultiAgentStreamState } from "@/types/assistant";

// Stable empty default
const EMPTY_STATE_BY_MESSAGE: Record<string, MultiAgentStreamState> = {};

export interface MultiAgentSnapshotsState {
  multiAgentStateByMessage: Record<string, MultiAgentStreamState>;
}

/**
 * Hook for managing multi-agent state snapshots
 */
export function useMultiAgentSnapshots(
  conversationId: string | undefined
): MultiAgentSnapshotsState {
  // Memoized selectors
  const snapshotsSelector = useMemo(
    () => selectMultiAgentByMessageId(conversationId),
    [conversationId]
  );
  const streamingSelector = useMemo(
    () => selectIsStreaming(conversationId),
    [conversationId]
  );
  const multiAgentSelector = useMemo(
    () => selectMultiAgent(conversationId),
    [conversationId]
  );
  const messagesSelector = useMemo(
    () => selectMessages(conversationId),
    [conversationId]
  );

  // Subscribe to store
  const savedSnapshots = useAssistantStore(snapshotsSelector);
  const isStreaming = useAssistantStore(streamingSelector);
  const multiAgent = useAssistantStore(multiAgentSelector);
  const messages = useAssistantStore(messagesSelector);

  // Store action
  const setMultiAgentSnapshot = useAssistantStore((s) => s.setMultiAgentSnapshot);

  // Use refs to track state for the snapshot effect
  // This avoids having multiAgent in the effect's dependency array
  const wasStreamingRef = useRef(false);
  const multiAgentRef = useRef(multiAgent);
  const messagesRef = useRef(messages);

  // Keep refs updated (but don't trigger effects)
  multiAgentRef.current = multiAgent;
  messagesRef.current = messages;

  // Snapshot ONLY on streaming end - dependency only on isStreaming and conversationId
  useEffect(() => {
    const wasStreaming = wasStreamingRef.current;
    wasStreamingRef.current = isStreaming;

    // Only act on streaming end transition
    if (wasStreaming && !isStreaming && conversationId) {
      const currentMultiAgent = multiAgentRef.current;
      const currentMessages = messagesRef.current;

      if (currentMultiAgent && currentMessages.length > 0) {
        const lastMessage = currentMessages[currentMessages.length - 1];
        if (lastMessage?.role === "assistant") {
          setMultiAgentSnapshot(conversationId, lastMessage.id, currentMultiAgent);
        }
      }
    }
  }, [isStreaming, conversationId, setMultiAgentSnapshot]);

  // Compute last assistant message ID for merging
  const lastAssistantMessageId = useMemo(() => {
    if (!messages.length) return undefined;
    const lastMsg = messages[messages.length - 1];
    return lastMsg?.role === "assistant" ? lastMsg.id : undefined;
  }, [messages]);

  // Merge live state with saved snapshots
  const multiAgentStateByMessage = useMemo(() => {
    if (!conversationId) return EMPTY_STATE_BY_MESSAGE;

    // During streaming, merge live state
    if (isStreaming && multiAgent && lastAssistantMessageId) {
      return {
        ...savedSnapshots,
        [lastAssistantMessageId]: multiAgent,
      };
    }

    // Not streaming - return saved snapshots (stable reference)
    return savedSnapshots;
  }, [conversationId, savedSnapshots, isStreaming, multiAgent, lastAssistantMessageId]);

  return { multiAgentStateByMessage };
}

export default useMultiAgentSnapshots;
